<script>
import { mapGetters } from 'vuex';

const SDK_SCRIPT_ID = 'cloudchat-support-sdk';
const READY_EVENT = 'cloudchat:ready';
const ON_START_CONVERSATION_EVENT = 'cloudchat:on-start-conversation';

export default {
  name: 'CloudchatSupportWidget',
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccount: 'getCurrentAccount',
    }),
  },
  mounted() {
    const token = window.chatwootConfig?.cloudchatSupportToken;
    const baseUrl = window.chatwootConfig?.cloudchatSupportBaseUrl;
    if (!token || !baseUrl) return;
    if (window.$cloudchat || document.getElementById(SDK_SCRIPT_ID)) {
      this.registerListeners();
      // SDK already initialized in a previous mount (e.g. logout → re-login
      // recreates this component while `cloudchat:ready` has already fired).
      // The ready listener will never re-trigger, so identify the current
      // user synchronously when the API is already available.
      if (window.$cloudchat?.setUser) this.applyCurrentUser();
      return;
    }

    // Hide the default bottom-right FAB — the sidebar rail button is the
    // single entry point. Must be set BEFORE sdk.js evaluates.
    window.cloudchatSettings = {
      ...(window.cloudchatSettings || {}),
      hideMessageBubble: true,
      position: 'left',
    };

    this.registerListeners();
    this.injectSdk(token, baseUrl);
  },
  beforeDestroy() {
    if (this.$_cloudchatReadyHandler) {
      window.removeEventListener(READY_EVENT, this.$_cloudchatReadyHandler);
      this.$_cloudchatReadyHandler = null;
    }
    if (this.$_cloudchatStartConversationHandler) {
      window.removeEventListener(
        ON_START_CONVERSATION_EVENT,
        this.$_cloudchatStartConversationHandler
      );
      this.$_cloudchatStartConversationHandler = null;
    }
  },
  methods: {
    injectSdk(token, baseUrl) {
      const d = document;
      const t = 'script';
      const g = d.createElement(t);
      const s = d.getElementsByTagName(t)[0];
      g.id = SDK_SCRIPT_ID;
      g.src = `${baseUrl}/packs/js/sdk.js?v=multi-1.0`;
      g.defer = true;
      g.async = true;
      if (s && s.parentNode) {
        s.parentNode.insertBefore(g, s);
      } else {
        (d.head || d.documentElement).appendChild(g);
      }
      g.onload = () => {
        if (!window.cloudchatSDK?.run) return;
        window.cloudchatSDK.run({
          websiteToken: token,
          baseUrl: baseUrl,
        });
      };
    },
    applyCurrentUser() {
      const email = this.currentUser?.email || '';
      if (!email || !window.$cloudchat?.setUser) return;
      // The support widget can be hmac_mandatory, so identity must be verified with an
      // identifier_hash. `cloudchat_support_identifier_hash` is computed server-side
      // from the support widget's own hmac_token (_user.json.jbuilder), so it always
      // matches what the widget backend validates regardless of CHATWOOT_INBOX_HMAC_KEY.
      window.$cloudchat.setUser(email, {
        email,
        name: this.currentUser?.name || '',
        // phone_number is not exposed by app/views/api/v1/models/_user.json.jbuilder.
        phone_number: '',
        identifier_hash:
          this.currentUser?.cloudchat_support_identifier_hash || undefined,
      });
    },
    registerListeners() {
      this.$_cloudchatReadyHandler = () => {
        this.applyCurrentUser();
      };
      this.$_cloudchatStartConversationHandler = () => {
        if (!window.$cloudchat?.setCustomAttributes) return;
        const attributes = this.buildCustomAttributes();
        if (!attributes) return;
        window.$cloudchat.setCustomAttributes(attributes);
      };
      window.addEventListener(READY_EVENT, this.$_cloudchatReadyHandler);
      window.addEventListener(
        ON_START_CONVERSATION_EVENT,
        this.$_cloudchatStartConversationHandler
      );
    },
    buildCustomAttributes() {
      const user = this.currentUser;
      if (!user?.id) return null;
      const account = this.currentAccount || {};

      const payload = {
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
        user_display_name: user.display_name,
        user_available_name: user.available_name,
        user_avatar_url: user.avatar_url,
        user_role: user.role,
        user_provider: user.provider,
        user_confirmed: user.confirmed,
        user_type: user.type,
        account_id: account.id,
        account_name: account.name,
        account_status: account.status,
        account_role: account.role,
      };

      const isScalar = value =>
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean';

      const mergeBag = (bag, prefix) => {
        if (!bag || typeof bag !== 'object') return;
        Object.keys(bag).forEach(key => {
          const value = bag[key];
          if (isScalar(value)) payload[`${prefix}${key}`] = value;
        });
      };

      mergeBag(user.custom_attributes, 'user_attr_');
      mergeBag(user.account_user_custom_attributes, 'account_user_attr_');
      mergeBag(account.custom_attributes, 'account_attr_');

      return Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      );
    },
  },
  render() {
    return null;
  },
};
</script>

<style lang="scss">
// Offset the cloudchat support widget so it clears the dashboard rail
// (PrimarySidebar w-16 = 64px + 13px margin). Targets the holder injected
// by sdk.js (app/javascript/sdk/IFrameHelper.js:91-92).
// `!important` needed because sdk.js injects `<style id="cw-widget-styles">`
// into <body> at runtime with `left: 20px` (same specificity, later in source).
// See app/javascript/sdk/sdk.js lines 146-153.
@media only screen and (min-width: 667px) {
  .woot-widget-holder.woot-elements--left {
    left: 77px !important;
  }
}
</style>
